import { z } from 'zod';
import { amountCents, email, isoDate, locale, uuid } from './common.js';
import { joinCode, teamName } from './teams.js';
import { voucherCode } from './voucher.js';

export const registrationStatus = z.enum([
  'pending', // created, awaiting payment
  'paid',
  'cancelled',
  'refunded',
  'expired', // hold released
]);
export type RegistrationStatus = z.infer<typeof registrationStatus>;

/**
 * The insured person's name (P-19, decision 0025 D1): the insurer needs a named holder, so
 * the name is asked on the email step, only when the insurance is taken.
 */
export const insuranceHolder = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
});
export type InsuranceHolder = z.infer<typeof insuranceHolder>;

/** Step 1 of checkout: email + formats + terms + optional referral code. */
export const createCheckoutInput = z
  .object({
    eventId: uuid,
    email,
    formatIds: z.array(uuid).min(1).max(5),
    acceptTerms: z.literal(true, {
      errorMap: () => ({ message: 'Terms must be accepted' }),
    }),
    /** Optional opt-in to the organiser's news; stored with timestamp and IP (P-02). */
    marketingOptIn: z.boolean().default(false),
    /** One voucher per order (P-03); re-validated and priced server-side. */
    voucherCode: voucherCode.optional(),
    /** Cancellation insurance for the whole order (P-19); priced server-side. */
    insurance: z.boolean().default(false),
    /** Required with `insurance`, ignored without (decision 0025 D1). */
    insuranceHolder: insuranceHolder.optional(),
    /**
     * Captain of a team format (P-20): the team's name; the format is then the order's only one.
     * Absent for a teammate paying their seat (P-21, `joinCode`) and for individual orders.
     */
    teamName: teamName.optional(),
    /**
     * Teammate paying their place from an invitation link (P-21): the seat's join code. The order
     * is then for the team's format alone, at the team's locked tier (record 0021 D1).
     */
    joinCode: joinCode.optional(),
    referralCode: z.string().trim().min(4).max(32).optional(),
    locale: locale.default('fr'),
    turnstileToken: z.string().min(1),
    utm: z
      .object({
        source: z.string().max(100).optional(),
        medium: z.string().max(100).optional(),
        campaign: z.string().max(100).optional(),
      })
      .optional(),
  })
  .refine((i) => !i.insurance || i.insuranceHolder !== undefined, {
    path: ['insuranceHolder'],
    message: 'The insured person is required',
  });
export type CreateCheckoutInput = z.infer<typeof createCheckoutInput>;

export const createCheckoutOutput = z.object({
  orderId: uuid,
  /** Null for an order at 0 €: confirmed without payment, go straight to /success. */
  clientSecret: z.string().nullable(),
  /** Sum of the lines before the voucher. */
  subtotalCents: amountCents,
  discountCents: amountCents,
  voucherCode: z.string().nullable(),
  /** Insurance premium (P-19): 0 when not taken; a rate of the formats after the voucher. */
  insuranceCents: amountCents,
  /** Amount of the PaymentIntent: formats − voucher + insurance; 0 under €0.50 (free). */
  totalCents: amountCents,
  expiresAt: isoDate,
  /**
   * One-time secret that lets this browser open a session once the order is paid
   * (`POST /orders/:id/session`). Kept in sessionStorage, never in a URL.
   */
  claimToken: z.string(),
  /** Team order (P-20, P-21): the team's name (typed by the captain, or joined by a teammate). */
  teamName: z.string().nullable(),
  lines: z.array(
    z.object({
      registrationId: uuid,
      formatId: uuid,
      formatName: z.string(),
      /** Null = show `formatName` (French) in English too. */
      formatNameEn: z.string().nullable(),
      tierName: z.string(),
      priceCents: amountCents,
    }),
  ),
});
export type CreateCheckoutOutput = z.infer<typeof createCheckoutOutput>;

export const orderStatus = z.enum(['pending', 'paid', 'failed', 'expired', 'refunded']);

export const orderSummary = z.object({
  orderId: uuid,
  status: orderStatus,
  email,
  totalCents: amountCents,
  paidAt: isoDate.nullable(),
  receiptUrl: z.string().url().nullable(),
  registrations: z.array(
    z.object({
      id: uuid,
      formatName: z.string(),
      /** Null = show `formatName` (French) in English too. */
      formatNameEn: z.string().nullable(),
      formatStartsAt: isoDate.nullable(),
      status: registrationStatus,
    }),
  ),
  referralCode: z.string().nullable(),
  /**
   * Team order (P-20, P-21): the team the captain's order created, or the one a teammate's
   * order pays a place in, so the confirmation screen knows what to show from the start. Null
   * for individual orders.
   */
  team: z
    .object({
      /** Null while the captain's order is pending: the team is created on payment. */
      id: uuid.nullable(),
      name: z.string(),
      sizeMin: z.number().int().positive(),
      /** Null = unlimited. */
      sizeMax: z.number().int().positive().nullable(),
      /** Seats on the roster: paid members plus pending invitations. */
      memberCount: z.number().int().nonnegative(),
      role: z.enum(['captain', 'member']),
    })
    .nullable(),
});
export type OrderSummary = z.infer<typeof orderSummary>;

export const claimOrderSessionInput = z.object({
  token: z.string().min(32).max(256),
});
export type ClaimOrderSessionInput = z.infer<typeof claimOrderSessionInput>;

/** POST /orders/:id/resend-confirmation: authorised by the same claim token (record 0026, D5). */
export const resendConfirmationInput = claimOrderSessionInput;
export type ResendConfirmationInput = ClaimOrderSessionInput;
